import type { Template } from '@/types/template';
import { getTemplateConfig } from '@/constants/templateConfig';
import { TemplateType, type BootData } from '@/types/boot';
import type { CheckoutData } from '@/types/checkout';
import { getDeviceType, isLandscape, isMobileDevice } from './device';
import type { TemplateInitContext } from '@/templates/CheckoutTemplate';
import { getCheckoutTemplate, TemplateRegistry } from '@/templates/TemplateRegistry';
import { logInfo } from './logger';

/**
 * The BE-issued wire format is the new {@link TemplateType} string enum
 * (introduced in PR #210: `'SINGLE_PAYMENT_METHOD'`, `'PREFERRED_PAYMENT_METHOD'`).
 * The pre-PR-#210 wire was a nested `{ type, onePaymentMethod }` object;
 * we still accept the object shape so playground patches / older unit
 * fixtures don't silently fall back to DEFAULT, but the `onePaymentMethod`
 * leg is ignored — the active method now lives exclusively on
 * `checkoutConfiguration.availablePaymentMethods[0]` and is read by each
 * template's `validatePreRender` / `filterAvailablePaymentMethods`
 * (see `SinglePaymentTemplate`).
 */
type TemplateInput = TemplateType | string | { type?: TemplateType | string };

/**
 * Resolves which template should be active given the boot configuration.
 *
 * Hardened against all of:
 *   - missing boot / missing checkoutConfiguration  → DEFAULT
 *   - missing or null template field                → DEFAULT
 *   - unrecognized template name (string we don't know about, e.g. BE
 *     rolls out a new template we haven't shipped support for yet)
 *     → DEFAULT + observability log so we notice in Coralogix
 *   - registered TemplateType but no template class registered yet
 *     → DEFAULT (via {@link TemplateRegistry.get})
 *   - any thrown error during eligibility check                → DEFAULT
 *   - `isEligible` returns false                               → walk
 *     `fallbackTemplateType` (PREFERRED → DEFAULT,
 *     EMBEDDED_PREFERRED_PAYMENT_METHOD → EMBEDDED_VIEW → DEFAULT)
 *
 * Hard configuration errors (e.g. SINGLE_PAYMENT_METHOD with no allow-list)
 * are surfaced separately by each template's `validatePreRender` — those
 * SHOULD show a clear error to the user rather than fall back silently.
 */
export const initializeTemplateFromBootConfig = (bootData?: BootData, checkoutData?: CheckoutData): Template => {
  try {
    const checkoutConfiguration = bootData?.checkoutConfiguration;
    const requested = checkoutConfiguration?.template as TemplateInput | undefined;
    if (!requested) {
      return getTemplateConfig(TemplateType.DEFAULT);
    }

    const requestedType = normalizeTemplateType(requested);
    if (requestedType === undefined) {
      logInfo('Unknown template name from boot — falling back to DEFAULT', {
        requested: String(typeof requested === 'object' ? requested.type : requested),
      });
      return getTemplateConfig(TemplateType.DEFAULT);
    }

    // Even if the type is in the enum, the registry may not have a class
    // registered yet (e.g. enum value added but template class missing).
    // `getCheckoutTemplate` falls back to DEFAULT in that case; double-check
    // and log so the gap is visible instead of silently rendering DEFAULT.
    if (!TemplateRegistry.has(requestedType)) {
      logInfo('Boot requested template has no registered class — falling back to DEFAULT', {
        requested: requestedType,
      });
      return getTemplateConfig(TemplateType.DEFAULT);
    }

    const template = getCheckoutTemplate(requestedType);
    const eligible = template.isEligible({
      isMobile: isMobileDevice(),
      isLandscape: isLandscape(),
      device: getDeviceType(),
      bootData,
      checkoutData,
      paymentMethods: checkoutData?.paymentMethods,
    });

    if (!eligible) {
      return resolveIneligibleFallback(template.type, {
        isMobile: isMobileDevice(),
        isLandscape: isLandscape(),
        device: getDeviceType(),
        bootData,
        checkoutData,
        paymentMethods: checkoutData?.paymentMethods,
      });
    }

    return getTemplateConfig(template.type);
  } catch (err) {
    // Last-resort defense: any throw during resolution must NOT crash the
    // checkout boot. Render DEFAULT and log so we can investigate.
    logInfo('initializeTemplateFromBootConfig threw — falling back to DEFAULT', {
      error: err instanceof Error ? err.message : String(err),
    });
    return getTemplateConfig(TemplateType.DEFAULT);
  }
};

/**
 * Walk {@link CheckoutTemplate.fallbackTemplateType} until a template is
 * eligible. The requested template has already failed `isEligible`. A cycle
 * or an unregistered hop stops the walk and returns DEFAULT, which is always
 * eligible and is every chain's terminal.
 */
const resolveIneligibleFallback = (
  startType: TemplateType,
  ctx: TemplateInitContext
): ReturnType<typeof getTemplateConfig> => {
  const seen = new Set<TemplateType>([startType]);
  let currentType = startType;
  const maxHops = TemplateRegistry.all().length;

  for (let hop = 0; hop < maxHops; hop += 1) {
    const nextType = getCheckoutTemplate(currentType).fallbackTemplateType;
    if (seen.has(nextType) || !TemplateRegistry.has(nextType)) break;
    seen.add(nextType);
    currentType = nextType;
    if (getCheckoutTemplate(currentType).isEligible(ctx)) {
      return getTemplateConfig(currentType);
    }
  }

  return getTemplateConfig(TemplateType.DEFAULT);
};

/**
 * The template the boot ASKED for, before eligibility is applied — `undefined`
 * when boot carries no template or an unrecognized name.
 *
 * For callers that must act before `/checkout/v3` lands (asset preloads), since
 * the resolved template additionally depends on `checkoutData`. A boot that asks
 * for a template it turns out to be ineligible for (EMBEDDED_VIEW on desktop) will
 * still be reported here, so a preload gated on this can be wasted but never
 * missing.
 */
export const getRequestedTemplateType = (bootData?: BootData | null): TemplateType | undefined =>
  normalizeTemplateType(bootData?.checkoutConfiguration?.template as TemplateInput);

/**
 * Templates for which a `validatePreRender` error is a hard checkout open
 * failure (no fallback template). Others only log and fall through to the
 * zero-methods / catch paths.
 */
const TEMPLATES_FAILING_ON_PRE_RENDER_ERROR: ReadonlySet<TemplateType> = new Set([TemplateType.SINGLE_PAYMENT_METHOD]);

export const shouldFailOnPreRenderError = (templateType: TemplateType): boolean =>
  TEMPLATES_FAILING_ON_PRE_RENDER_ERROR.has(templateType);

/**
 * Returns the matching {@link TemplateType} for a wire value, or `undefined`
 * if the input is something we don't recognize. Returning undefined (rather
 * than silently coercing to DEFAULT) lets the caller log + react to unknowns.
 */
const normalizeTemplateType = (template: TemplateInput): TemplateType | undefined => {
  if (template && typeof template === 'object') {
    return normalizeTemplateType(template.type ?? TemplateType.DEFAULT);
  }
  if (typeof template !== 'string' || template.length === 0) return undefined;

  // Exact match on the live enum values (post-PR-210 wire).
  if (isTemplateType(template)) return template;

  // Backward-compat: pre-PR-210 wire used kebab/snake strings.
  switch (template) {
    case 'one_payment':
      return TemplateType.SINGLE_PAYMENT_METHOD;
    case 'preferred_payment_method':
      return TemplateType.PREFERRED_PAYMENT_METHOD;
    case 'embedded_view':
      return TemplateType.EMBEDDED_VIEW;
    case 'embedded_preferred_payment_method':
      return TemplateType.EMBEDDED_PREFERRED_PAYMENT_METHOD;
    case 'default':
      return TemplateType.DEFAULT;
  }

  // Forward-compat: accept TypeScript enum *keys* (caller might pass
  // `'SINGLE_PAYMENT_METHOD'` as a literal key rather than its value).
  // Unknown strings fall through to `undefined` so the caller can log.
  return TemplateType[template as keyof typeof TemplateType];
};

const isTemplateType = (template: string): template is TemplateType => {
  return Object.values(TemplateType).includes(template as TemplateType);
};
